import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Spinner, Alert, Badge, Button, Dropdown, ButtonGroup } from "react-bootstrap";
import NewsMetaHelmet from "../components/NewsMetaHelmet";
import { getApiBase, getNewsShareUrl, shareNewsTo } from "../utils/share";
import "./NewsDetails.css";

const BASE = getApiBase();
const API_URL = `${BASE}/api/news`;

const getImage = (item) => {
    const placeholder = "https://placehold.co/1200x650?text=No+Image";
    if (!item) return placeholder;

    const resolveUrl = (path) => {
        if (!path || typeof path !== "string" || path.trim() === "") return null;
        const p = path.trim();

        if (p.startsWith("http://") || p.startsWith("https://")) return p;
        if (p.startsWith("/uploads/")) return `${BASE}${p}`;
        if (p.startsWith("uploads/")) return `${BASE}/${p}`;
        if (p.includes(".")) return `${BASE}/uploads/${p}`;

        return null;
    };

    return (
        resolveUrl(item.imagePath) ||
        resolveUrl(item.image) ||
        resolveUrl(item.imageUrl) ||
        resolveUrl(item.imageURL) ||
        resolveUrl(item.thumbnail) ||
        resolveUrl(item.photo) ||
        (Array.isArray(item.images) && item.images.length > 0 ? resolveUrl(item.images[0]) : null) ||
        placeholder
    );
};

const handleImgError = (e) => {
    e.target.onerror = null;
    e.target.src = "https://placehold.co/1200x650?text=Image+Not+Found";
};

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const getAuthor = (item) => item?.author || item?.writer || item?.authorName || "";
const getDescription = (item) => item?.shortDesc || item?.description || item?.summary || "";
const getContentParagraphs = (content) => {
    if (typeof content !== "string" || !content.trim()) return [];
    return content
        .split(/\r?\n\s*\r?\n|\r?\n/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);
};

const ShareMenu = ({ item }) => (
    <Dropdown as={ButtonGroup} className="share-group">
        <Button size="sm" variant="success" className="share-btn" onClick={() => shareNewsTo("whatsapp", item)}>
            <i className="bi bi-share-fill me-1" />
            Share
        </Button>
        <Dropdown.Toggle split size="sm" variant="success" />
        <Dropdown.Menu align="end" className="share-dropdown">
            <Dropdown.Item className="share-item whatsapp" onClick={() => shareNewsTo("whatsapp", item)}>
                <i className="bi bi-whatsapp me-2" />
                WhatsApp
            </Dropdown.Item>
            <Dropdown.Item className="share-item facebook" onClick={() => shareNewsTo("facebook", item)}>
                <i className="bi bi-facebook me-2" />
                Facebook
            </Dropdown.Item>
            <Dropdown.Item className="share-item x" onClick={() => shareNewsTo("x", item)}>
                <i className="bi bi-twitter-x me-2" />
                X (Twitter)
            </Dropdown.Item>
            <Dropdown.Item className="share-item telegram" onClick={() => shareNewsTo("telegram", item)}>
                <i className="bi bi-telegram me-2" />
                Telegram
            </Dropdown.Item>
            <Dropdown.Item className="share-item linkedin" onClick={() => shareNewsTo("linkedin", item)}>
                <i className="bi bi-linkedin me-2" />
                LinkedIn
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item className="share-item email" onClick={() => shareNewsTo("email", item)}>
                <i className="bi bi-envelope-fill me-2" />
                Email
            </Dropdown.Item>
            <Dropdown.Item className="share-item copy" onClick={() => shareNewsTo("copy", item)}>
                <i className="bi bi-link-45deg me-2" />
                Copy Link
            </Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
);

const NewsDetails = () => {
    const { id } = useParams();
    const [news, setNews] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const cleanId = String(id || "").trim();
        if (!cleanId) {
            setNews(null);
            setError("News not found");
            setLoading(false);
            return undefined;
        }

        const controller = new AbortController();
        setLoading(true);
        setError("");

        fetch(`${API_URL}/${encodeURIComponent(cleanId)}`, { signal: controller.signal })
            .then((res) => {
                if (!res.ok) throw new Error("News not found");
                return res.json();
            })
            .then((result) => {
                const article = result?.data || result?.news || result;
                if (!article || (!article.title && !article.content)) {
                    throw new Error(result?.message || "News not found");
                }
                setNews(article);
                fetch(`${API_URL}/views/${cleanId}`, { method: "PUT" }).catch(() => {});
            })
            .catch((err) => {
                if (err.name === "AbortError") return;

                // If running locally and remote fetch failed, try local backend
                if (typeof window !== "undefined" && window.location.hostname === "localhost" && !API_URL.includes("localhost:8000")) {
                    fetch(`http://localhost:8000/api/news/${encodeURIComponent(cleanId)}`)
                        .then((res) => {
                            if (!res.ok) throw new Error("News not found");
                            return res.json();
                        })
                        .then((result) => {
                            const article = result?.data || result?.news || result;
                            if (article && (article.title || article.content)) {
                                setNews(article);
                                setError("");
                                fetch(`http://localhost:8000/api/news/views/${cleanId}`, { method: "PUT" }).catch(() => {});
                                return;
                            }
                            throw new Error();
                        })
                        .catch(() => {
                            setNews(null);
                            setError("Unable to load news details.");
                        })
                        .finally(() => setLoading(false));
                    return;
                }

                setNews(null);
                setError("Unable to load news details.");
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, [id]);

    if (loading) {
        return (
            <Container className="news-details-state py-5 text-center">
                <Spinner animation="border" role="status" />
                <p className="mt-3 mb-0">Loading news...</p>
            </Container>
        );
    }

    if (error) {
        return <Container className="news-details-state py-5 text-center">
            <Alert variant="danger">News not found</Alert>
            <Link to="/news" className="news-details-back">&larr; Back to News</Link>
        </Container>;
    }

    if (!news) return null;

    const description = getDescription(news);
    const author = getAuthor(news);
    const publishedDate = formatDate(news.createdAt || news.publishedAt || news.date);
    const contentParagraphs = getContentParagraphs(news.content);
    const shareUrl = getNewsShareUrl(news);

    return (
        <>
            <NewsMetaHelmet
                title={news.title || "News Article"}
                description={description}
                image={getImage(news)}
                url={shareUrl}
            />

            <Container className="news-details-page py-5">
                <div className="news-details-toolbar">
                    <Link to="/news" className="news-details-back">&larr; Back to News</Link>
                    <ShareMenu item={news} />
                </div>

                <article className="news-details-article">
                    {news.category && <Badge bg="danger" className="news-details-category">{news.category}</Badge>}

                    <h1>{news.title || "Untitled news"}</h1>

                    {(author || publishedDate) && (
                        <div className="news-details-meta">
                            {author && <span>By {author}</span>}
                            {publishedDate && <span>{publishedDate}</span>}
                        </div>
                    )}

                    <img
                        src={getImage(news)}
                        alt={news.title || "News"}
                        className="news-details-image"
                        onError={handleImgError}
                    />

                    {description && <p className="news-details-description">{description}</p>}

                    <div className="news-details-content">
                        {contentParagraphs.length > 0
                            ? contentParagraphs.map((paragraph, index) => (
                                <p key={`${index}-${paragraph.slice(0, 20)}`}>{paragraph}</p>
                            ))
                            : <p>No content available.</p>}
                    </div>
                </article>
            </Container>
        </>
    );
};

export default NewsDetails;
